import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Search, Trash2, UserPlus, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import PageLayout from "@/components/PageLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProspectsPage() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    contact: "",
    address: "",
    interest_level: "low",
    notes: ""
  });

  useEffect(() => {
    fetchProspects();
  }, []);

  const fetchProspects = async (search = "") => {
    try {
      setLoading(true);
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/prospects`, { 
        params,
        withCredentials: true 
      });
      setProspects(response.data);
    } catch (error) {
      console.error("Failed to fetch prospects", error);
      toast.error("Failed to load prospects");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    fetchProspects(query);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/prospects`, formData, { withCredentials: true });
      toast.success("Prospect added successfully");
      setDialogOpen(false);
      setFormData({
        name: "",
        contact: "",
        address: "",
        interest_level: "low",
        notes: ""
      });
      fetchProspects();
    } catch (error) {
      console.error("Failed to create prospect", error);
      toast.error("Failed to add prospect");
    }
  };

  const deleteProspect = async (prospectId) => {
    if (!window.confirm("Are you sure you want to delete this prospect?")) return;
    
    try {
      await axios.delete(`${API}/prospects/${prospectId}`, { withCredentials: true });
      toast.success("Prospect deleted");
      fetchProspects();
    } catch (error) {
      console.error("Failed to delete prospect", error);
      toast.error("Failed to delete prospect");
    }
  };

  return (
    <PageLayout title="Prospects Database">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="search-prospects-input"
            type="text"
            placeholder="Search prospects..."
            value={searchQuery}
            onChange={handleSearch}
            className="pl-10"
          />
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-prospect-button" className="bg-emerald-700 hover:bg-emerald-800">
              <Plus className="w-4 h-4 mr-2" />
              Add Prospect
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Prospect</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <Input
                  data-testid="prospect-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact</label>
                <Input
                  data-testid="prospect-contact-input"
                  value={formData.contact}
                  onChange={(e) => setFormData({...formData, contact: e.target.value})}
                  placeholder="Phone or email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <Input
                  data-testid="prospect-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="Location"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Interest Level</label>
                <Select value={formData.interest_level} onValueChange={(value) => setFormData({...formData, interest_level: value})}>
                  <SelectTrigger data-testid="prospect-interest-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <Textarea
                  data-testid="prospect-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="How you met, interests, follow-up plans..."
                  rows={4}
                />
              </div>
              <Button data-testid="submit-prospect-button" type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800">
                Add Prospect
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
        </div>
      ) : prospects.length === 0 ? (
        <div className="text-center py-12">
          <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No prospects added yet</p>
          <p className="text-sm text-gray-400 mt-1">Start building your prospects database</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {prospects.map(prospect => (
            <ProspectCard 
              key={prospect.id}
              prospect={prospect}
              onDelete={() => deleteProspect(prospect.id)}
            />
          ))}
        </div>
      )}
    </PageLayout>
  );
}

function ProspectCard({ prospect, onDelete }) {
  const interestColors = {
    low: "bg-gray-100 text-gray-700",
    medium: "bg-blue-100 text-blue-700",
    high: "bg-green-100 text-green-700"
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900">{prospect.name}</h3>
            <span className={`text-xs px-2 py-1 rounded-full capitalize ${interestColors[prospect.interest_level || 'low']}`}>
              {prospect.interest_level || 'low'} interest
            </span>
          </div>
          
          {prospect.contact && (
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <Mail className="w-4 h-4 mr-1" />
              {prospect.contact}
            </div>
          )}
          {prospect.address && (
            <div className="flex items-center text-sm text-gray-600 mb-3">
              <MapPin className="w-4 h-4 mr-1" />
              {prospect.address}
            </div>
          )}
          {prospect.notes && (
            <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{prospect.notes}</p>
          )}
        </div>
        <Button
          data-testid={`prospect-delete-${prospect.id}`}
          onClick={onDelete}
          variant="ghost"
          size="sm"
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
